// src/UserProfile.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Post from './Post';
import './UserProfile.css';
import backIcon from './assets/backspace-icon.png';
import mystoreIcon from './assets/mystore-icon.png';
import api from './api';
import { usePostContext } from './context/PostContext';
import { useUserPostContext } from './context/UserPostContext';
import FollowDrawer from './components/FollowDrawer';
import ReadMoreText from './common/ReadMoreText';
import UserProfileSkeleton from './components/skeleton/UserProfileSkeleton';
import RoleInfoDrawer from './components/popups/RoleInfoDrawer';

// Tabs
import FilterTabs from './components/FilterTabs';
import AboutSection from './components/profile/AboutSection';
import AssetsTab from './components/profile/AssetsTab';

// at top with other imports
import StakeDetailsDrawer from './components/popups/StakeDetailsDrawer';
import BadgePills from './components/profile/BadgePills';
import RewardCodeRow from './components/profile/RewardCodeRow';
import goldTickIcon from './assets/goldtick-icon.png';
import globeIcon from './assets/globe-icon.png';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProfileBannerHeader from './components/ProfileBannerHeader';
import ProfileAvatarViewer from './components/ProfileAvatarViewer';

import { canAccessStudio } from './studio/studioAccess';

import {
  createStudioAnalyticsEvent,
  trackStudioEvent,
} from './studio/avatar/services/studioAnalyticsApi';

import placeholderProfile from './assets/placeholder-profile.png';


import PioneerSummaryPanel from './components/PioneerSummaryPanel';
import pioneerSummaryIcon from './assets/pioneersummary-icon.png';
import {
  recordPioneerSummaryEvent,
} from './services/pioneerSummaryApi';

const DEBUG = true;

const PROFILE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

function getProfileCacheKey(username, viewerUsername) {
  const cleanUsername = String(username || '').trim().toLowerCase();
  const cleanViewer = String(viewerUsername || 'guest').trim().toLowerCase();

  // viewerUsername is included because isFollowing/followsMe depends on who is viewing
  return `viewedUserProfile:${cleanViewer}:${cleanUsername}`;
}

function readCachedProfile(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt || 0);

    if (!cachedAt) return null;

    const isFresh = Date.now() - cachedAt <= PROFILE_CACHE_TTL_MS;
    if (!isFresh) return null;

    return parsed;
  } catch (err) {
    console.warn('⚠️ Failed to read cached profile:', err);
    return null;
  }
}

function saveCachedProfile(cacheKey, data) {
  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        ...data,
        cachedAt: Date.now(),
      })
    );
  } catch (err) {
    console.warn('⚠️ Failed to save cached profile:', err);
  }
}


function getSafeAppUrl(userData) {
  const raw = userData?.appUrl || userData?.domain || '';
  const trimmed = String(raw || '').trim();

  if (!trimmed) return '';

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

function UserProfile({ currentUser }) {
  const {
    userPosts,
    setUserPosts,
    page,
    setPage,
    hasMore,
    setHasMore,
    clearUserPostState,
  } = useUserPostContext();

  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getPostInteractions } = usePostContext();

  const [userData, setUserData] = useState(null);

const [loading, setLoading] = useState(true);
const [loadingPosts, setLoadingPosts] = useState(false);
const [cacheEntryAnimation, setCacheEntryAnimation] = useState(false);

  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);

  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [followsMe, setFollowsMe] = useState(false);
  const [showRoleInfo, setShowRoleInfo] = useState(false);


const [showPioneerSummary, setShowPioneerSummary] = useState(false);

const [showAvatarViewer, setShowAvatarViewer] = useState(false);

useEffect(() => {
  setShowAvatarViewer(false);
}, [username]);

const [userRewardCodes, setUserRewardCodes] = useState([]);
const [codesLoaded, setCodesLoaded] = useState(false);


  // Tabs
  const [selectedTab, setSelectedTab] = useState('posts');


const [pillHidden, setPillHidden] = useState(false);
const lastScrollTopRef = useRef(0);

  // Refs to control pagination + boot

const feedRef = useRef(null);
const topbarRef = useRef(null);
const roleRef = useRef(null);
const sentinelRef = useRef(null);


  const observerRef = useRef(null); // <— NEW
  const pageRef = useRef(1);
  const loadingPostsRef = useRef(false);

const hasMoreRef = useRef(true);
const bootFetchInFlight = useRef(false);
const usedCachedProfileRef = useRef(false);
const isSelf = currentUser?.username === username;

const [stakeDetailsOpen, setStakeDetailsOpen] = useState(false);
const [stakeDetailsPostId, setStakeDetailsPostId] = useState(null);



const openStakeDetails = (postId) => {
  setStakeDetailsPostId(postId);
  setStakeDetailsOpen(true);
};



useEffect(() => {
  if (loading || !userData) return;

  const scrollArea = feedRef.current;
  const topbar = topbarRef.current;
  const roleElement = roleRef.current;

  if (!scrollArea || !topbar || !roleElement) return;

  let animationFrameId = null;

  const updateTopbarFade = () => {
    const scrollTop = Math.max(0, scrollArea.scrollTop);

    const roleTop = roleElement.offsetTop;
    const fadeStart = 0;
    const fadeEnd = Math.max(100, roleTop - 60);

    const rawProgress =
      (scrollTop - fadeStart) / (fadeEnd - fadeStart);

    const progress = Math.min(
      1,
      Math.max(0, rawProgress)
    );

    topbar.style.setProperty(
      '--userprofile-topbar-progress',
      String(progress)
    );

    animationFrameId = null;
  };

  const handleTopbarScroll = () => {
    if (animationFrameId !== null) return;

    animationFrameId =
      window.requestAnimationFrame(updateTopbarFade);
  };

  updateTopbarFade();

  scrollArea.addEventListener('scroll', handleTopbarScroll, {
    passive: true,
  });

  window.addEventListener('resize', handleTopbarScroll);

  return () => {
    scrollArea.removeEventListener('scroll', handleTopbarScroll);
    window.removeEventListener('resize', handleTopbarScroll);

    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
    }
  };
}, [loading, userData?.username]);


  useEffect(() => {
    pageRef.current = page;
    hasMoreRef.current = hasMore;
  }, [page, hasMore]);




// ✅ Mark this author's posts as "seen" when viewing their profile
  useEffect(() => {
    if (!username || !currentUser?.username) return;

    const markSeen = async () => {
      try {
        await api.post('/users/activity/seen-author', {
          authorUsername: username,
        });
        if (DEBUG) console.log('[UP] marked posts seen for', username);
      } catch (err) {
        console.error('❌ Failed to mark author seen:', err);
      }
    };

    markSeen();
  }, [username, currentUser?.username]);



useEffect(() => {
  if (!username) return;

  const loadUserCodes = async () => {
    try {
      const res = await api.get(`/rewards/codes/user/${username}`);
      const items = res?.data?.items || [];
      setUserRewardCodes(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('❌ Failed to load user reward codes:', err);
      setUserRewardCodes([]);
    } finally {
      setCodesLoaded(true);
    }
  };

  // ✅ load on username change
  loadUserCodes();

  // ✅ also reload whenever user opens Assets tab (so buy button reflects reveal/redeem instantly)
}, [username]);



useEffect(() => {
  if (!username) return;
  if (selectedTab !== 'assets') return;

  const refresh = async () => {
    try {
      const res = await api.get(`/rewards/codes/user/${username}`);
      const items = res?.data?.items || [];
      setUserRewardCodes(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('❌ Failed to refresh user reward codes:', err);
    } finally {
      setCodesLoaded(true);
    }
  };

  refresh();
}, [selectedTab, username]);




// Reset profile state when username changes
useEffect(() => {
  if (!username) return;

  bootFetchInFlight.current = false;

  setError(null);
  setUserRewardCodes([]);
  setCodesLoaded(false);

  const cacheKey = getProfileCacheKey(username, currentUser?.username);
  const cached = readCachedProfile(cacheKey);

if (cached?.userData) {
  usedCachedProfileRef.current = true;

  // ✅ Restart the smooth cached-entry animation
  setCacheEntryAnimation(false);
  requestAnimationFrame(() => {
    setCacheEntryAnimation(true);
  });

  if (DEBUG) console.log('📦 [UP] Loaded cached profile for:', username);

    setUserData(cached.userData);
    setFollowers(Array.isArray(cached.followers) ? cached.followers : []);
    setFollowing(Array.isArray(cached.following) ? cached.following : []);
    setIsFollowing(Boolean(cached.isFollowing));
    setFollowsMe(Boolean(cached.followsMe));

    const cachedPosts = Array.isArray(cached.posts) ? cached.posts : [];
    setUserPosts(cachedPosts);

    const cachedPage = Number(cached.page || 1);
    setPage(cachedPage);
    pageRef.current = cachedPage;

    const cachedHasMore =
      typeof cached.hasMore === 'boolean' ? cached.hasMore : true;

    setHasMore(cachedHasMore);
    hasMoreRef.current = cachedHasMore;

    // ✅ Important: no skeleton when cache exists
    setLoading(false);


} else {
  usedCachedProfileRef.current = false;
  setCacheEntryAnimation(false);

  if (DEBUG) console.log('🕳️ [UP] No cached profile for:', username);

    // old safe behavior only when no cache exists
    setLoading(true);
    setUserData(null);
    setFollowers([]);
    setFollowing([]);
    setIsFollowing(false);
    setFollowsMe(false);

    clearUserPostState();

    pageRef.current = 1;
    setPage(1);
    setHasMore(true);
    hasMoreRef.current = true;
  }

  if (feedRef.current) {
    feedRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }

  setSelectedTab('posts');

  if (DEBUG) console.log('[UP] Reset for username:', username);
}, [
  username,
  currentUser?.username,
  clearUserPostState,
  setHasMore,
  setPage,
  setUserPosts,
]);



  // Initial fetch (profile + first page + follow details)
  useEffect(() => {
    if (!username || !currentUser?.username) return;
    if (bootFetchInFlight.current) return;
    bootFetchInFlight.current = true;



const fetchData = async () => {
  try {
    if (!usedCachedProfileRef.current) {
      setLoading(true);
    }

    if (DEBUG) {
      console.log('[UP] Fetch page 1 for', username, {
        usedCache: usedCachedProfileRef.current,
      });
    }


const [profileRes, aboutRes, postsRes] = await Promise.all([
  api.get(`/users/${username}`),
  api.get(`/users/${username}/about`),
  api.get(`/posts/user/${username}?page=1&limit=10&username=${encodeURIComponent(currentUser.username)}`)
]);


const profileData = profileRes?.data || {};
const aboutData = aboutRes?.data || {};

const mergedUserData = {
  ...profileData,

  // ✅ make AboutSection receive the new backend fields
  accountType: aboutData.accountType || profileData.accountType || 'primary',
  apps: Array.isArray(aboutData.apps) ? aboutData.apps : [],

  // ✅ keep existing profile.about fields, but overlay normalized About API data
  about: {
    ...(profileData.about || {}),
    languages: Array.isArray(aboutData.languages) ? aboutData.languages : [],
    links: aboutData.links || (profileData.about?.links || {}),
    badges: Array.isArray(aboutData.badges) ? aboutData.badges : [],
  },
};

setUserData(mergedUserData);


        const raw = Array.isArray(postsRes.data) ? postsRes.data : postsRes.data.posts || [];
        const enriched = await Promise.all(
          raw.map(async (post) => {
            const out = { ...post };
            try {
              const res = await getPostInteractions(post._id, true);
              out.likes = res.likes;
              out.comments = res.comments;
              out.likesCount = res.likes.length;
              out.commentsCount = res.comments.length;
              out.userHasLiked = res.likes.some(u => u.username === currentUser.username);
            } catch {
              out.likes = [];
              out.comments = [];
              out.likesCount = 0;
              out.commentsCount = 0;
              out.userHasLiked = false;
            }
            out.timestamp = post.createdAt;
            out.type = 'post';


            out.user = post.user || {
              name: post.name,
              username: post.username,
              profilePic: post.profilePic,
              userType: post.userType,
              isMerchant: post.isMerchant,
              isAdmin: post.isAdmin,
              adminRole: post.adminRole,
              isApp: post.isApp,
              accountType: post.accountType,
              location: post.location,
            };

            return out;
          })
        );

        setUserPosts(enriched);
        setPage(1);
        pageRef.current = 1;

        const more1 = enriched.length === 10;
        setHasMore(more1);
        hasMoreRef.current = more1;
        if (DEBUG) console.log('[UP] Page 1 set. hasMore=', more1, 'posts=', enriched.length);


        // Follow details
        try {
          console.log('🟦 [UserProfile] fetching full follow data for:', username);

          const followRes = await api.get(`/follow/full-follow-data/${username}`);

          const followersArr = Array.isArray(followRes.data?.followers) ? followRes.data.followers : [];
          const followingArr = Array.isArray(followRes.data?.following) ? followRes.data.following : [];

          console.log('🟩 [UserProfile] full follow data response:', {
            followers: followersArr,
            following: followingArr,
          });


setFollowers(followersArr);
setFollowing(followingArr);

const theyFollowMe = followingArr.some(u => u.username === currentUser.username);
setFollowsMe(theyFollowMe);

const backendIsFollowing = followersArr.some(f => f.username === currentUser.username);
setIsFollowing(backendIsFollowing);

// ✅ Save fresh profile result into localStorage cache
const cacheKey = getProfileCacheKey(username, currentUser?.username);

saveCachedProfile(cacheKey, {
  userData: mergedUserData,
  posts: enriched,
  followers: followersArr,
  following: followingArr,
  isFollowing: backendIsFollowing,
  followsMe: theyFollowMe,
  page: 1,
  hasMore: more1,
});


          localStorage.setItem(
            `${currentUser.username}-isFollowing-${username}`,
            JSON.stringify(backendIsFollowing)
          );

          const stored = localStorage.getItem(`${currentUser.username}-isFollowing-${username}`);
          if (stored !== null) {
            setIsFollowing(JSON.parse(stored));
          }
        } catch (err) {
          console.error('❌ [UserProfile] Failed to fetch full follow details:', err);
        }

        setError(null);
      } catch (err) {
        console.error('❌ Error loading profile:', err);
        setError('User not found or server error.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username, currentUser?.username, getPostInteractions, setHasMore, setPage, setUserPosts]);

  // Deep link: scroll to a specific post after load
  useEffect(() => {
    const scrollToPostId = location.state?.scrollToPostId;
    if (!scrollToPostId) return;

    const wrapperId = `post-${scrollToPostId}`;
    let observer = null;
    let heightCheckInterval = null;

    const scrollToStablePost = () => {
      const postEl = document.getElementById(wrapperId);
      if (!postEl || !feedRef.current) return;

      let lastHeight = postEl.offsetHeight;
      let stableCount = 0;

      heightCheckInterval = setInterval(() => {
        const currentHeight = postEl.offsetHeight;
        if (currentHeight === lastHeight) {
          stableCount++;
        } else {
          stableCount = 0;
          lastHeight = currentHeight;
        }
        if (stableCount >= 2) {
          clearInterval(heightCheckInterval);
          const offset = postEl.offsetTop;
          feedRef.current.scrollTo({ top: offset - 100, behavior: 'smooth' });
          postEl.classList.add('highlight-flash');
          setTimeout(() => postEl.classList.remove('highlight-flash'), 3000);
        }
      }, 100);
    };

    const immediate = document.getElementById(wrapperId);
    if (immediate) {
      scrollToStablePost();
      return () => heightCheckInterval && clearInterval(heightCheckInterval);
    }

    observer = new MutationObserver(() => {
      const postEl = document.getElementById(wrapperId);
      if (postEl) {
        scrollToStablePost();
        observer.disconnect();
      }
    });

    observer.observe(document.getElementById('root') || document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (observer) observer.disconnect();
      if (heightCheckInterval) clearInterval(heightCheckInterval);
    };
  }, [userPosts, location.state]);

  // Enricher for pagination pages
  const enrichPagePosts = useCallback(async (rawPosts) => {
    const enriched = await Promise.all(
      rawPosts.map(async (post) => {
        const out = { ...post };
        try {
          const res = await getPostInteractions(post._id, true);
          out.likes = res.likes;
          out.comments = res.comments;
          out.likesCount = res.likes.length;
          out.commentsCount = res.comments.length;
          out.userHasLiked = res.likes.some(u => u.username === currentUser.username);
        } catch {
          out.likes = [];
          out.comments = [];
          out.likesCount = 0;
          out.commentsCount = 0;
          out.userHasLiked = false;
        }
        out.timestamp = post.createdAt;
        out.type = 'post';


        out.user = post.user || {
          name: post.name,
          username: post.username,
          profilePic: post.profilePic,
          userType: post.userType,
          isMerchant: post.isMerchant,
          isAdmin: post.isAdmin,
          adminRole: post.adminRole,
          isApp: post.isApp,
          accountType: post.accountType,
          location: post.location,
        };


        return out;
      })
    );
    return enriched;
  }, [currentUser?.username, getPostInteractions]);

  // Load more — guarded & page bump after success
  const loadMorePosts = useCallback(async () => {
    if (loadingPostsRef.current) return;
    if (!hasMoreRef.current) return;

    loadingPostsRef.current = true;
    setLoadingPosts(true);

    try {
      const nextPage = (pageRef.current || 1) + 1;
      if (DEBUG) console.log('[UP] loadMore start -> page', nextPage);

      const res = await api.get(`/posts/user/${username}?page=${nextPage}&limit=10&username=${encodeURIComponent(currentUser.username)}`)
      const raw = Array.isArray(res.data) ? res.data : res.data.posts || [];
      const enriched = await enrichPagePosts(raw);

      const newUnique = enriched.filter(p => !userPosts.some(e => e._id === p._id));
      if (newUnique.length) {
        if (DEBUG) console.log('[UP] appending', newUnique.length, 'posts');
        setUserPosts(prev => [...prev, ...newUnique]);
      } else {
        if (DEBUG) console.log('[UP] no unique posts to append');
      }

      pageRef.current = nextPage;
      setPage(nextPage);

      const moreN = enriched.length === 10;
      setHasMore(moreN);
      hasMoreRef.current = moreN;
      if (DEBUG) console.log('[UP] page', nextPage, 'hasMore=', moreN, 'returned=', enriched.length);
    } catch (err) {
      console.error('❌ Failed to load more posts:', err);
    } finally {
      setLoadingPosts(false);
      loadingPostsRef.current = false;
      if (DEBUG) console.log('[UP] loadMore done');
    }
  }, [enrichPagePosts, setHasMore, setPage, setUserPosts, userPosts, username]);

  // Scroll listener — backup to IO (guards at top)
  const handleScroll = useCallback(() => {
    if (selectedTab !== 'posts') return;
    if (!hasMoreRef.current || loadingPostsRef.current) return;

    const area = feedRef.current;
    if (!area) return;

    const containerIsScrollable = area.scrollHeight > area.clientHeight + 2;

    const scrollTop = containerIsScrollable
      ? area.scrollTop
      : (window.pageYOffset || document.documentElement.scrollTop || 0);
    const clientHeight = containerIsScrollable
      ? area.clientHeight
      : window.innerHeight;
    const scrollHeight = containerIsScrollable
      ? area.scrollHeight
      : document.documentElement.scrollHeight;

    const nearBottom = scrollTop + clientHeight >= scrollHeight - 300;

    if (DEBUG) {
      console.log('[UP] scroll check',
        { containerIsScrollable, scrollTop, clientHeight, scrollHeight, nearBottom,
          hasMore: hasMoreRef.current, inflight: loadingPostsRef.current });
    }

    if (nearBottom) {
      loadMorePosts();
    }
  }, [loadMorePosts, selectedTab]);

  // Attach listeners to BOTH container and window (backup)
  useEffect(() => {
    const area = feedRef.current;
    if (!area) return;
    const onScroll = () => handleScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    area.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    const t = setTimeout(onScroll, 300);

    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      area.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll]);


useEffect(() => {
  if (loading) return;

  const area = feedRef.current;
  if (!area) return;

  const handlePillScroll = () => {
    const scrollTop = Math.max(0, area.scrollTop);
    const prevTop = lastScrollTopRef.current || 0;
    const delta = scrollTop - prevTop;

    const THRESHOLD = 8;
    const ALWAYS_SHOW_TOP = 40;

    if (scrollTop <= ALWAYS_SHOW_TOP) {
      setPillHidden(false);
    } else if (Math.abs(delta) > THRESHOLD) {
      if (delta > 0) {
        setPillHidden(true);
      } else {
        setPillHidden(false);
      }
    }

    lastScrollTopRef.current = scrollTop;
  };

  lastScrollTopRef.current = Math.max(0, area.scrollTop);

  area.addEventListener('scroll', handlePillScroll, { passive: true });
  handlePillScroll();

  return () => {
    area.removeEventListener('scroll', handlePillScroll);
  };
}, [loading, selectedTab]);



  // ====== FIX: ref-callback that attaches IO when sentinel mounts ======
  const setSentinelNode = useCallback((node) => {
    // cleanup old observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    sentinelRef.current = node;

    // only attach when on Posts tab and node exists
    if (!node || selectedTab !== 'posts') return;

    node.style.width = '100%';
    node.style.height = '1px';

    const rootEl = feedRef.current || null;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasMoreRef.current || loadingPostsRef.current) return;
        loadMorePosts();
      },
      {
        root: rootEl,
        rootMargin: '800px 0px',
        threshold: 0.01,
      }
    );

    io.observe(node);
    observerRef.current = io;
    if (DEBUG) console.log('[UP] IO attached via ref-callback');
  }, [selectedTab, loadMorePosts]);

  // rebind IO when switching back to Posts (if node already mounted)
  useEffect(() => {
    if (selectedTab === 'posts' && sentinelRef.current && !observerRef.current) {
      setSentinelNode(sentinelRef.current);
    }
    if (selectedTab !== 'posts' && observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [selectedTab, setSentinelNode]);
  // ================================================================

  // SHORT-CONTENT AUTO-TRIGGER: if page 1 doesn't fill viewport
  useEffect(() => {
    if (selectedTab !== 'posts') return;
    const area = feedRef.current;
    if (!area) return;

    const containerIsScrollable = area.scrollHeight > area.clientHeight + 2;
    if (DEBUG) console.log('[UP] short-content check: scrollable=', containerIsScrollable,
      'hasMore=', hasMoreRef.current, 'inflight=', loadingPostsRef.current);

    if (!containerIsScrollable && hasMoreRef.current && !loadingPostsRef.current) {
      if (DEBUG) console.log('[UP] short-content -> auto loadMore');
      loadMorePosts();
    }
  }, [selectedTab, userPosts.length, loadMorePosts]);

  // Delete post handler
  const handleDeletePost = useCallback((deletedPostId) => {
    setUserPosts(prev => prev.filter(p => p._id !== deletedPostId));
  }, [setUserPosts]);

  // Follow toggle (optimistic)
  const toggleFollow = useCallback(async () => {
    if (!currentUser || !username) return;
    if (isFollowLoading) return;

    setIsFollowLoading(true);
    const next = !isFollowing;
    setIsFollowing(next);

    setFollowers(prev => {
      if (next) {
        if (!prev.some(u => u.username === currentUser.username)) {
          return [

            { username: currentUser.username, name: currentUser.name, profilePic: currentUser.profilePic },

            ...prev,
          ];
        }
        return prev;
      } else {
        return prev.filter(u => u.username !== currentUser.username);
      }
    });

    try {
      await api.post('/follow/toggle', {
        follower: currentUser.username,
        following: username,
      });

      localStorage.setItem(`${currentUser.username}-isFollowing-${username}`, JSON.stringify(next));

const cacheKey = getProfileCacheKey(username, currentUser?.username);
const cached = readCachedProfile(cacheKey);

if (cached?.userData) {
  const updatedFollowers = next
    ? [
        {
          username: currentUser.username,
          name: currentUser.name,
          profilePic: currentUser.profilePic,
        },
        ...(Array.isArray(cached.followers) ? cached.followers : []).filter(
          u => u.username !== currentUser.username
        ),
      ]
    : (Array.isArray(cached.followers) ? cached.followers : []).filter(
        u => u.username !== currentUser.username
      );

  saveCachedProfile(cacheKey, {
    ...cached,
    followers: updatedFollowers,
    isFollowing: next,
  });
}


      if (next && !isSelf) {
        await api.post('/notify', {
          type: 'follow',
          fromUser: currentUser.username,
          toUser: username,
          postId: null,
          text: `${currentUser.username} started following you`,
        });
      }
    } catch (err) {
      console.error('❌ Follow toggle error:', err);
      // Revert UI
      setIsFollowing(!next);
      setFollowers(prev => {
        if (next) {
          return prev.filter(u => u.username !== currentUser.username);
        } else {
          if (!prev.some(u => u.username === currentUser.username)) {
            return [

              { username: currentUser.username, name: currentUser.name, profilePic: currentUser.profilePic },

              ...prev,
            ];
          }
          return prev;
        }
      });
    } finally {
      setIsFollowLoading(false);
    }
  }, [currentUser, isFollowLoading, isFollowing, username, isSelf]);

  if (error) return <div className="error-message">{error}</div>;


if (loading || !userData) {
  return (
    <div className="userprofile-screen-container">
      <div className="userprofile-topbar">
        <button
          type="button"
          className="userprofile-topbar-icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <img
            src={backIcon}
            alt=""
            className="userprofile-back-icon"
          />
        </button>

        <span>{username}</span>
      </div>

      <div className="userprofile-scroll-area" ref={feedRef}>
        <UserProfileSkeleton />
      </div>
    </div>
  );
}

  const { name, profilePic, bio, location: userLocation, userType } = userData;

  const profileIsAdmin = userData?.isAdmin === true || userData?.adminRole != null;
  const profileAdminRole = userData?.adminRole || null;

  const profileIsApp =
    userData?.isApp === true ||
    userData?.accountType === 'app';

  const isApprovedMerchant = userType === 'merchant' && userData?.isMerchant;

  const role =
    profileIsAdmin
      ? 'Admin'
      : profileIsApp
      ? 'App'
      : isApprovedMerchant
      ? 'Merchant'
      : 'Pioneer';


const equippedBanner = userData?.equippedProfileBanner || null;

const hasEquippedBanner = Boolean(
  equippedBanner?.imageUrl
);


  const showSuspendedStorePopup = () => alert('⚠️ This merchant’s store has been suspended by BarterFi moderators.');
  const canVisitStore = userData?.userType === 'merchant'
    && userData?.isMerchant === true
    && userData?.status === 'approved';

  const canVisitApp = profileIsApp && userData?.canVisitApp === true;
  const visitAppUrl = getSafeAppUrl(userData);

const handleVisitApp = () => {
  if (!visitAppUrl) return;

  const cleanUrl = String(visitAppUrl).trim();

  console.log('🌍 Visiting app:', cleanUrl);
  console.log('appUrl:', userData?.appUrl);
  console.log('domain:', userData?.domain);

  try {
    if (window.top) {
      window.top.location.replace(cleanUrl);
      return;
    }
  } catch (err) {
    console.warn('window.top replace failed:', err);
  }

  try {
    window.open(cleanUrl, '_top');
    return;
  } catch (err) {
    console.warn('window.open _top failed:', err);
  }

  try {
    window.location.replace(cleanUrl);
  } catch (err) {
    console.error('❌ Failed to open external app:', err);
    window.location.href = cleanUrl;
  }
};


  return (


<div
  className={`userprofile-screen-container ${
    hasEquippedBanner
      ? 'userprofile-screen-container-with-banner'
      : ''
  }`}
>





<div
  className="userprofile-topbar"
  ref={topbarRef}
>


<button
  type="button"
  className="userprofile-topbar-icon-btn"
  onClick={() => navigate(-1)}
  aria-label="Go back"
>
  <img
    src={backIcon}
    alt=""
    className="userprofile-back-icon"
  />
</button>


  <span className="userprofile-topbar-title">
    {username}
  </span>


        {isApprovedMerchant && (
          <button
            className="visit-store-btn"
            onClick={() =>
              canVisitStore
                ? navigate(`/store/${userData.businessUsername}`)
                : showSuspendedStorePopup()
            }
          >
            <img src={mystoreIcon} alt="Store" className="visit-store-icon" />
            Visit Store
          </button>
        )}

        {canVisitApp && (
          <button
            className="visit-store-btn"
            onClick={handleVisitApp}
          >
            <img src={globeIcon} alt="App" className="visit-store-icon" />
            Visit App
          </button>
        )}
      </div>


<div
  className={`userprofile-scroll-area ${
    cacheEntryAnimation ? 'userprofile-cache-content-enter' : ''
  }`}
  ref={feedRef}
>

 


<div
  className={`profile-header-centered ${
    hasEquippedBanner ? 'profile-header-centered-with-banner' : ''
  }`}
>



{hasEquippedBanner ? (
  <ProfileBannerHeader
    banner={equippedBanner}
    profilePic={profilePic || placeholderProfile}
    name={name}
    isOwnProfile={false}
    isSpecialAvatar={profileIsAdmin || profileIsApp}
    onViewAvatar={() => setShowAvatarViewer(true)}
  />
) : (
  <button
    type="button"
    className={`userprofile-avatar-view-btn ${
      profileIsAdmin || profileIsApp
        ? 'userprofile-avatar-view-btn-special'
        : ''
    }`}
    onClick={() => setShowAvatarViewer(true)}
    aria-label={`View ${name || username} profile picture`}
    aria-haspopup="dialog"
  >
    <img
      src={profilePic || placeholderProfile}
      alt={`${name || username} avatar`}
      className={`avatar-large ${
        profileIsAdmin || profileIsApp
          ? 'avatar-large-admin'
          : ''
      }`}
    />
  </button>
)}



<div className="profile-name-row">
  <strong className="profile-name">{name}</strong>

  {(profileIsAdmin || profileIsApp) && (
    <img
      src={goldTickIcon}
      alt={profileIsAdmin ? 'Admin verified' : 'App verified'}
      className="profile-gold-tick"
      title={profileIsAdmin ? (profileAdminRole || 'Admin') : 'App account'}
    />
  )}

</div>


<span
  className="role-badge"
  ref={roleRef}
  onClick={() => setShowRoleInfo(true)}
  style={{ cursor: 'pointer' }}
>
  {role}
</span>


          {bio && <div className="user-bio"><ReadMoreText text={bio} maxLines={3} /></div>}
          {userLocation && <p className="location-text">{userLocation}</p>}

          <div className="follow-stats-centered">
            <span className="follower-link" onClick={() => setShowFollowers(true)}>
              {followers.length} Followers
            </span> •
            <span className="follower-link" onClick={() => setShowFollowing(true)}>
              {following.length} Following
            </span>
          </div>
        </div>

        {/* Follow + Message buttons */}
        {!isSelf && (
          <div className="follow-btn-container">
            <button
              className="follow-button"
              onClick={toggleFollow}
              disabled={isFollowLoading}
            >
              {isFollowLoading
                ? 'Loading...'
                : isFollowing
                  ? 'Unfollow'
                  : (followsMe ? 'Follow back' : 'Follow')}
            </button>
            <button
              className="message-button"
              onClick={() => navigate(`/chat/${username}`)}
            >
              Message
            </button>
          </div>
        )}

        {/* TABS */}
        <div style={{ padding: '8px 14px 0' }}>



<FilterTabs
  tabs={[
    { key: 'posts', label: 'Posts' },
    { key: 'badges', label: 'Badges' },
    { key: 'assets', label: 'Assets' },
    { key: 'about', label: 'About' },
  ]}
  value={selectedTab}
  onChange={setSelectedTab}
  ariaLabel="User profile sections"
  layout="fixed"
/>


        </div>

        {/* TAB CONTENT */}
        {selectedTab === 'about' && (
          <AboutSection
            user={userData}
            canVisitStore={canVisitStore}
            onVisitStore={() => canVisitStore && navigate(`/store/${userData.businessUsername}`)}
            isOwner={false}
          />
        )}


{selectedTab === 'badges' && (
  <div className="badge-tab-wrap">
    <BadgePills
      badges={userData?.badges || []}
      username={username}
      stacked       
      animate     
    />
  </div>
)}



{selectedTab === 'assets' && (
  <div className="assets-tab-wrap">
    <RewardCodeRow
      items={userRewardCodes}
      mode="viewer"
      onBuy={(code) => {
        // ✅ viewer can only buy if backend says canBuy
        if (!code?.canBuy) return;
        console.log('buy placeholder', code);
      }}
    />
  </div>
)}



{selectedTab === 'posts' && (
  <div className="user-posts userprofile-post-feed">

{userPosts.map(post => (



<Post
  key={`${post._id}-${post.createdAt || ''}`}
  {...post}
  viewCount={Number(post.viewCount || 0)}
  currentUser={currentUser.username}

  currentUserProfilePic={currentUser?.profilePic}
  currentScreen="userprofile"


  isApp={post.isApp}
  accountType={post.accountType}

    wrapperId={`post-${post._id}`}
    onDelete={handleDeletePost}

    onStakeDetailsClick={() => openStakeDetails(post._id)}
    stakeActivePi={Number(post.stakeActivePi || 0)}
    stakeNextExpiryAt={post.stakeNextExpiryAt || null}
  />
))}



{loadingPosts && <LoadingSpinner size="medium" />}

    {!hasMore && !loadingPosts && userPosts.length > 0 && (
      <div className="end-of-feed-msg">
        <p>You've reached the end.</p>
      </div>
    )}

    {/* Sentinel to trigger page 2+ (ref-callback attaches IO ASAP) */}
    <div ref={setSentinelNode} style={{ height: 1 }} />
  </div>
)}



      </div>

      <FollowDrawer
        isOpen={showFollowers}
        onClose={() => setShowFollowers(false)}
        title="Followers"
        list={followers}
      />
      <FollowDrawer
        isOpen={showFollowing}
        onClose={() => setShowFollowing(false)}
        title="Following"
        list={following}
      />
      <RoleInfoDrawer
        isOpen={showRoleInfo}
        role={role.toLowerCase()}
        onClose={() => setShowRoleInfo(false)}
      />


<StakeDetailsDrawer
  isOpen={stakeDetailsOpen}
  postId={stakeDetailsPostId}
  onClose={() => setStakeDetailsOpen(false)}
/>


{/* 🔥 Floating App CTA Pill */}

{canVisitApp && (
  <div
    className={`app-cta-pill ${pillHidden ? 'is-hidden' : ''}`}
    onClick={handleVisitApp}
  >
    <img
      src={profilePic || 'https://via.placeholder.com/100'}
      alt="app"
      className="app-cta-avatar"
    />

    <div className="app-cta-text">
      <span className="app-cta-title">Learn more</span>
      <span className="app-cta-sub">Visit official app</span>
    </div>
  </div>
)}



<button
  type="button"


className={`userprofile-summary-fab ${
  canVisitApp ? 'above-app-cta' : ''
} ${pillHidden || showAvatarViewer ? 'is-hidden' : ''}`}

  onClick={() => {
    setShowPioneerSummary(true);

    recordPioneerSummaryEvent(
      username,
      'summary_opened',
      'user_profile'
    ).catch((err) => {
      console.warn(
        'Summary open event unavailable:',
        err?.response?.status || err?.message
      );
    });
  }}
  aria-label={
    isSelf
      ? 'View my summary'
      : `Get to know ${name || username}`
  }
  title={
    isSelf
      ? 'View my summary'
      : `Get to know ${name || username}`
  }
>
  <img src={pioneerSummaryIcon} alt="" />
</button>


{showPioneerSummary && (

<PioneerSummaryPanel
  username={username}
  entryPoint="user_profile"
  onClose={() => setShowPioneerSummary(false)}
    onFollow={
      !isSelf && !isFollowing
        ? () => {
            toggleFollow();
            setShowPioneerSummary(false);
          }
        : undefined
    }
    onMessage={() => {
      setShowPioneerSummary(false);
      navigate(`/chat/${encodeURIComponent(username)}`);
    }}
  />
)}



{showAvatarViewer && (
  <ProfileAvatarViewer
    key={username}
    profilePic={profilePic}
    name={name || username}
    onClose={() => setShowAvatarViewer(false)}


    onCreateAvatar={
      canAccessStudio(currentUser)
        ? () => {
            void trackStudioEvent(
              createStudioAnalyticsEvent(
                'studio_entry_clicked',
                { entryPoint: 'user_profile' }
              )
            );

            setShowAvatarViewer(false);

            navigate('/studio/avatar', {
              state: {
                studioEntryPoint: 'user_profile',
              },
            });
          }
        : undefined
    }

  />
)}


    </div>
  );
}

export default UserProfile;
